import { useCallback, useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Button,
  Checkbox,
  TableSortLabel,
  TextField,
  Autocomplete,
  Pagination,
  TablePagination,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Stack } from "@mui/system";
import { useSelector } from "react-redux";
import {
  setCampaignToScopeLoading,
  setSortCampaignsWithoutScopeData,
} from "../../../../../../../../../../features/scopesTabSlice";
import { useDispatch } from "react-redux";
import { formatTwoDecimal } from "../../../../../../../../../../utils/helpers";
import { STOperationEndPointLogic } from "../../../../../../../../../../logic/PaidTrafficManagement/SearchTermOperation/searchTermOperationEndPointLogic";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../../../../../../features/userSlice";
import { useScopes } from "../../../../../../STOperationhooks/useScopes";

const tableHeadings = {
  campaign_id: "Campaign ID",
  campaign_name: "Campaign Name",
  clicks_last_30_days: "Clicks last 30 days",
  cost_last_30_days: "Cost Last 30 Days",
  convertions_last_30_days: "Conversions Last 30 Days",
  add_to_scope: "Add to Scope",
};

// headers for the table columns with sortable option for each column
const tableheaders = [
  { id: 1, KEY: "campaign_id", label: "Campaign ID", sortable: true },
  {
    id: 2,
    KEY: "campaign_name", // Use the nested key
    label: "Campaign Name",
    sortable: true,
  },
  {
    id: 3,
    KEY: "clicks_last_30_days", // Use the nested key
    label: "Clicks last 30 days",
    sortable: true,
  },
  {
    id: 4,
    KEY: "cost_last_30_days", // Use the nested key
    label: "Cost Last 30 Days",
    sortable: true,
  },
  {
    id: 5,
    KEY: "convertions_last_30_days", // Use the nested key
    label: "Conversions Last 30 Days",
    sortable: true,
  },
  {
    id: 6,
    KEY: "add_to_scope", // Use the nested key
    label: "Add to Scope",
    sortable: false,
  },
];

const CampaignsWithoutScopeTable = () => {
  const dispatch = useDispatch();
  const [sortedData, setSortedData] = useState<any[]>([]);
  const headCells = Object.entries(tableHeadings);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [checkedRows, setCheckedRows] = useState<{ [key: string]: boolean }>(
    {},
  ); // state to track checked rows
  const [selectedScopes, setSelectedScopes] = useState<{
    [campaignSharedUid: string]: string | null;
  }>({}); // state to track selected scopes from autocomplete
  const [errors, setErrors] = useState<{ [campaignSharedUid: string]: string }>(
    {},
  ); // state to track errors for autocomplete

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    scopesOfSTOperation,
    campaignsWithoutScopeData,
    sortCampaignsWithoutScopeData,
    campaignToScopeLoading,
    newScopeLoading,
    editScopeLoading,
  } = useSelector((state: any) => state.searchTermOperation);

  const { campaignsWithoutScope, scopeList } = useScopes();

  // options for autocomplete
  const scopeOptions = Array.isArray(scopesOfSTOperation)
    ? scopesOfSTOperation.map((s: any) => ({
        scope_name: s?.name ?? "",
        scope_shared_uid: s?.shared_uid ?? null,
      }))
    : [];

  //checkbox click
  const handleCheck = (campaignSharedUid: string) => {
    setCheckedRows((prev) => {
      const isChecked = !prev[campaignSharedUid]; // toggle state

      // if unchecked → also clear scope + error
      if (!isChecked) {
        setSelectedScopes((prevScopes) => {
          const updated = { ...prevScopes };
          delete updated[campaignSharedUid];
          return updated;
        });

        //clear error when unchecked
        setErrors((prevErrors) => {
          const updated = { ...prevErrors };
          delete updated[campaignSharedUid];
          return updated;
        });
      }

      return {
        ...prev,
        [campaignSharedUid]: isChecked,
      };
    });
  };

  // selection of scopes
  const handleScopeChange = (
    campaignSharedUid: string,
    scopeSharedUid: string | null,
  ) => {
    setSelectedScopes((prev) => ({
      ...prev,
      [campaignSharedUid]: scopeSharedUid,
    }));

    // Clear error for this row if user selects a scope
    setErrors((prev) => {
      const updated = { ...prev };
      if (scopeSharedUid) {
        delete updated[campaignSharedUid]; // remove error for this row
      }
      return updated;
    });
  };

  // disable button
  const isButtonDisabled = !Object.entries(checkedRows).some(
    ([campaignSharedUid, isChecked]) =>
      isChecked && selectedScopes[campaignSharedUid], // must be checked and have a scope
  );

  //-- Sorting Data
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      if (
        !sortCampaignsWithoutScopeData.keytoSort ||
        !sortCampaignsWithoutScopeData.direction
      ) {
        // Default sorting by modified_at (To make new items appear first in your table when they are Created or Added)
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sortCampaignsWithoutScopeData.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue, bValue;

        aValue = a[keytoSort];
        bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortCampaignsWithoutScopeData.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sortCampaignsWithoutScopeData.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        } else {
          return sortCampaignsWithoutScopeData.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sortCampaignsWithoutScopeData],
  );

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    dispatch(
      setSortCampaignsWithoutScopeData({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sortCampaignsWithoutScopeData.keytoSort &&
          sortCampaignsWithoutScopeData.direction === "asc"
            ? "desc"
            : "asc",
      }),
    );
  };

  //-- Handle Save Button Click(link between campaign and scope)
  const handleSave = async () => {
    if (newScopeLoading || editScopeLoading) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Campaign linking or unlinking is in progress. Please wait until it’s complete before starting another.",
          type: "warning",
        }),
      );
      return;
    }

    const newErrors: { [campaignSharedUid: string]: string } = {};

    Object.entries(checkedRows).forEach(([campaignSharedUid, isChecked]) => {
      if (isChecked && !selectedScopes[campaignSharedUid]) {
        newErrors[campaignSharedUid] = "Please select a scope before saving.";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors); // show inline errors
      return; // stop API call
    }

    try {
      dispatch(setCampaignToScopeLoading(true)); // Start loading

      // Group campaigns by scope_shared_uid
      const groupedByScope = Object.entries(selectedScopes)
        .filter(
          ([campaignSharedUid, scopeSharedUid]) =>
            checkedRows[campaignSharedUid] && scopeSharedUid,
        )
        .reduce(
          (acc, [campaignSharedUid, scopeSharedUid]) => {
            if (!acc[scopeSharedUid!]) acc[scopeSharedUid!] = [];
            acc[scopeSharedUid!].push(campaignSharedUid);
            return acc;
          },
          {} as { [scopeSharedUid: string]: string[] },
        );

      // Make API calls for each scope group
      const allResponses = await Promise.all(
        Object.entries(groupedByScope).map(
          async ([scopeSharedUid, campaignSharedUids]) => {
            const linkReqParams = {
              scope_shared_uid: scopeSharedUid,
              campaign_shared_uid: campaignSharedUids,
            };
            const res =
              await STOperationEndPointLogic.linkBetweenCampaignToScope(
                oauthTokenHeader,
                linkReqParams,
              );
            return res;
          },
        ),
      );

      // Flatten data from all responses
      const flattened = allResponses.flatMap(
        (res) => res?.data?.data || res?.data || [],
      );

      // Analyze responses
      const all201 = allResponses.every((r) => r.status === 201);
      const hasPartial = allResponses.some(
        (r) => r.status === 207 && r.partialSuccess,
      );
      const allFailed = allResponses.every(
        (r) => r.status === 207 && r.allFailed,
      );
      const any400 = allResponses.some((r) => r.status === 400);

      // All successful (201)
      if (all201) {
        setCheckedRows({});
        setSelectedScopes({});
        setErrors({});
        await campaignsWithoutScope();
        await scopeList();
        dispatch(setCampaignToScopeLoading(false)); // Stop loading

        dispatch(
          setDisplayErrorSnackBar({
            message: "campaign's linked successfully.",
            type: "success",
          }),
        );
      }
      //  Partial successes (207)
      else if (hasPartial) {
        const successCount = flattened.filter(
          (item: any) => item.created === true || item.updated === true,
        ).length;
        const failCount = flattened.filter(
          (item: any) => item.created === false && item.updated === false,
        ).length;

        setCheckedRows({});
        setSelectedScopes({});
        setErrors({});
        await campaignsWithoutScope();
        await scopeList();
        dispatch(setCampaignToScopeLoading(false)); // Stop loading

        dispatch(
          setDisplayErrorSnackBar({
            message: `${successCount} campaign(s) linked/updated, ${failCount} failed.`,
            type: "warning",
          }),
        );
      }
      //  All failed (207)
      else if (allFailed) {
        dispatch(setCampaignToScopeLoading(false)); // Stop loading
        dispatch(
          setDisplayErrorSnackBar({
            message: "All campaign link/update operations failed.",
            type: "error",
          }),
        );
      }
      //  Bad request (400)
      else if (any400) {
        dispatch(setCampaignToScopeLoading(false)); // Stop loading
        dispatch(
          setDisplayErrorSnackBar({
            message: "Bad request - invalid data provided.",
            type: "error",
          }),
        );
      }
      //  Unexpected response
      else {
        dispatch(setCampaignToScopeLoading(false)); // Stop loading
        dispatch(
          setDisplayErrorSnackBar({
            message:
              "Unexpected response from server while linking campaigns to scopes.",
            type: "error",
          }),
        );
      }
    } catch (error: any) {
      dispatch(setCampaignToScopeLoading(false));
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.reason ||
            "Failed to link campaigns to scopes. Please try again.",
          type: "error",
        }),
      );
    }
  };

  //-- Set Sorted Data
  useEffect(() => {
    const sortedRows = getSortedData(campaignsWithoutScopeData || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [campaignsWithoutScopeData, getSortedData]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <>
      <TableContainer>
        <Stack flexDirection={"column"} alignItems={"flex-end"} gap={2}>
          <Table aria-label="collapsible table" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {tableheaders.map((header: any) => (
                  <TableCell key={header.id} align="left">
                    <TableSortLabel
                      active={
                        sortCampaignsWithoutScopeData.keytoSort === header.KEY
                      }
                      direction={
                        sortCampaignsWithoutScopeData.keytoSort === header.KEY
                          ? sortCampaignsWithoutScopeData.direction
                          : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData?.length > 0 ? (
                sortedData.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells?.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "add_to_scope" ? (
                          <Stack direction="row" alignItems="center" gap={1}>
                            {/* Checkbox */}
                            <Checkbox
                              checked={
                                !!checkedRows[row["campaign_shared_uid"]]
                              }
                              onChange={() =>
                                handleCheck(row["campaign_shared_uid"])
                              }
                              color="secondary"
                              size="small"
                              sx={{ padding: 0 }}
                            />
                            {/* Autocomplete */}
                            <Autocomplete
                              data-tracker="501"
                              size="small"
                              disabled={
                                !checkedRows[row["campaign_shared_uid"]]
                              } // disable until checkbox is checked
                              options={scopeOptions}
                              getOptionLabel={(option: any) =>
                                option?.scope_name || ""
                              }
                              value={
                                scopeOptions?.find(
                                  (option: any) =>
                                    option?.scope_shared_uid ===
                                    selectedScopes[row["campaign_shared_uid"]],
                                ) || null
                              }
                              onChange={(_, newValue) =>
                                handleScopeChange(
                                  row["campaign_shared_uid"],
                                  newValue?.scope_shared_uid ?? null,
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select from list"
                                  variant="standard"
                                  error={!!errors[row["campaign_shared_uid"]]}
                                  helperText={
                                    errors[row["campaign_shared_uid"]] || ""
                                  }
                                  size="small"
                                  sx={{
                                    fontSize: "12px",
                                  }}
                                  InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                  }}
                                  onKeyDown={(e) => {
                                    // Prevent all keyboard input
                                    e.preventDefault();
                                  }}
                                />
                              )}
                              sx={{ minWidth: 180 }}
                            />
                          </Stack>
                        ) : key === "campaign_id" ? (
                          <> {row[key] ?? ""}</>
                        ) : key === "campaign_name" ? (
                          <> {row[key] ?? ""}</>
                        ) : key === "clicks_last_30_days" ? (
                          <> {row[key] ?? ""}</>
                        ) : key === "cost_last_30_days" ? (
                          <>
                            {" "}
                            {row[key] ? formatTwoDecimal(row[key], "$") : ""}
                          </>
                        ) : key === "convertions_last_30_days" ? (
                          <>
                            {" "}
                            {row[key] ? formatTwoDecimal(row[key], "%") : ""}
                          </>
                        ) : (
                          ""
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Button
            data-tracker="502"
            variant="contained"
            color="secondary"
            disabled={isButtonDisabled || campaignToScopeLoading}
            onClick={handleSave}
            endIcon={<SaveOutlinedIcon />}
          >
            save & refresh
          </Button>
        </Stack>
      </TableContainer>
      {sortedData.length > 0 && (
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 1 }}
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": { display: "none" },
              ".MuiTablePagination-toolbar": { paddingLeft: "16px" },
            }}
          />

          <Pagination
            count={Math.ceil(sortedData.length / rowsPerPage)}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
            showFirstButton
            showLastButton
            color="secondary"
            size="small"
          />

          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
             classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": { display: "none" },
              ".MuiTablePagination-input": { marginRight: "15px" },
            }}
          />
        </Stack>
      )}
    </>
  );
};

export default CampaignsWithoutScopeTable;
